import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Security as SecurityIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import chatService from '@/service/chat.service';

const TwoFactorCodeModal = ({ 
  open, 
  onClose, 
  onSuccess, 
  fileName,
  s3Path,
  role
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // Call the backend API to verify 2FA code and get file URL
      const result = await chatService.verifyFileAccess(verificationCode, s3Path, role);
      
      toast.success('File access verified successfully');
      onSuccess(result.file_url);
      onClose();
    } catch (error) {
      console.error('Error verifying code:', error);
      setError(error.message || 'Invalid verification code');
      toast.error('Failed to verify code');
    } finally {
      setVerificationCode('');
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setVerificationCode('');
    setError('');
    onClose();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6" component="div">
              Two-Factor Authentication
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            To access the file <strong>"{fileName}"</strong>, please enter your 6-digit verification code from your authenticator app.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Verification Code"
          value={verificationCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setVerificationCode(value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          placeholder="Enter 6-digit code"
          inputProps={{
            maxLength: 6,
            style: { 
              textAlign: 'center',
              fontSize: '1.2rem',
              letterSpacing: '0.2em'
            }
          }}
          disabled={isLoading}
          autoFocus
        />

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Enter the 6-digit code from your authenticator app
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={isLoading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={isLoading || verificationCode.length !== 6}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Verifying...' : 'Verify & Open File'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorCodeModal;
